import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Column,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TableCell,
  TableRow,
} from '@tempmee/react';
import { usePromoCodeTargetOffices } from '../../../../domain/Campaigns';
import useClampedPage from '../../../../hooks/useClampedPage';
import { DebouncedSearchField } from '../../../commonComponents/DebouncedSearchField';
import { DsPagination } from '../../../commonComponents/DsPagination';
import { TableCardFrame } from '../../../commonComponents/TableCardFrame';

const COLUMNS = ['Name', 'Office ID'];

const countLabel = (count) => `${count} ${count === 1 ? 'office' : 'offices'}`;

/**
 * The Office List viewer (GOTU-9810): every office a promo code's rule names by
 * `office_id`, with names, searchable and paged server-side.
 *
 * The page mounts it only while it is open, so each opening starts on page 1
 * with an empty search. A new search term re-slices the list, so it resets to
 * the first page — page 3 of the whole list is not page 3 of the matches.
 *
 * The search box sits outside the table frame so it stays mounted through
 * every state: an admin whose term matched nothing needs it to try another.
 */
export const PromoCodeTargetOfficesDialog = ({ open, onClose, campaignId }) => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');

  const {
    offices, totalCount, pageSize, isPending, isError,
  } = usePromoCodeTargetOffices(campaignId, { page, search });

  // A page can outlive its data if the rule is edited while the viewer is
  // open. Unknown totals (loading, failed) never clamp.
  useClampedPage(page, isError ? null : totalCount, pageSize ?? 1, setPage);

  const handleSearchChange = useCallback((value) => {
    setSearch(value);
    setPage(1);
  }, []);

  return (
    <Dialog open={open} onClose={onClose} size="large">
      <DialogTitle closeLabel="Dismiss dialog">Office List</DialogTitle>

      <DialogContent>
        <Column gap={3}>
          <DebouncedSearchField
            label="Search"
            placeholder="Search by id or office name"
            value={search}
            onChange={handleSearchChange}
          />

          {/* The DS Dialog panel has no max height, and it sits in a fixed,
              centred overlay with the body scroll locked — a full page of
              offices would run off-screen with no way to reach it. The
              list scrolls here instead. */}
          <Box className="overflow-y-auto max-h-96">
            <TableCardFrame
              title={totalCount === null || isError ? null : countLabel(totalCount)}
              columns={COLUMNS}
              rowCount={offices.length}
              isPending={isPending}
              isError={isError}
              emptyMessage={search ? 'No offices match this search.' : 'This code names no offices.'}
              errorMessage="Could not load the office list."
              footer={pageSize ? (
                <DsPagination
                  page={page}
                  pageSize={pageSize}
                  totalCount={totalCount ?? 0}
                  onPageChange={setPage}
                />
              ) : null}
            >
              {offices.map((office) => (
                <TableRow key={office.officeId}>
                  <TableCell>{office.displayName}</TableCell>
                  <TableCell>{office.officeId}</TableCell>
                </TableRow>
              ))}
            </TableCardFrame>
          </Box>
        </Column>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" color="secondary" size="small" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

PromoCodeTargetOfficesDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  campaignId: PropTypes.string.isRequired,
};

export default PromoCodeTargetOfficesDialog;
